import { MSXAuthService } from './service-auth-msx';
import { AuthServiceBase } from './service-auth-base';
import { isValidJwt } from './utils-auth';
import { isMsx } from '../../utils/utils-msx';
import serviceLogger from '../logging/service-logger';

export const AUTH_INVALID_TOKEN = 'AuthService: Token is not a valid JWT';

export class AuthService {
  /** The singleton instance of this class. */
  private static instance: AuthService;

  /** The singleton instance of the auth service to use */
  private static authServiceInstance: AuthServiceBase;

  public haveFallbacked: boolean = false;

  // Constructor
  constructor() {
    AuthService.authServiceInstance = new MSXAuthService();
  }

  // STATIC METHODS
  /**
   * Static method to get class instance.
   */
  public static get Instance() {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // PUBLIC METHODS
  public get MEM_AUTH_SCOPE(): string {
    return AuthService.authServiceInstance.MEM_AUTH_SCOPE;
  }

  public get GRAPH_AUTH_SCOPE(): string {
    return AuthService.authServiceInstance.GRAPH_AUTH_SCOPE;
  }

  /** Shared MSX→MEM fallback promise so concurrent callers converge on a single swap. */
  private fallbackPromise: Promise<void> | null = null;

  public async fallbackToMemAuthService(): Promise<void> {
    // Idempotent: concurrent callers (e.g. a burst of API requests all failing MSX at once in
    // standalone) share one fallback instead of the second caller being rejected outright.
    if (!this.fallbackPromise) {
      this.haveFallbacked = true;
      this.fallbackPromise = import('./service-auth-mem')
        .then((module) => {
          AuthService.authServiceInstance = new module.MEMAuthService();
        })
        .catch((error: Error) => {
          // Allow a later attempt to retry the fallback if the dynamic import failed.
          this.fallbackPromise = null;
          this.haveFallbacked = false;
          throw error;
        });
    }
    return this.fallbackPromise;
  }

  public async getLoggedAccountInfo(): Promise<any> {
    return new Promise((resolve, reject) => {
      AuthService.authServiceInstance
        .getAccountInfo()
        .then((accountInfo: any) => {
          resolve(accountInfo);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }

  public async getHeaderForScope(scope: string): Promise<string> {
    return new Promise((resolve, reject) => {
      AuthService.authServiceInstance
        .getTokenForScope(scope)
        .then((token: string) => {
          if (!isValidJwt(token)) {
            const segments = typeof token === 'string' ? token.split('.').length : 0;
            const len = typeof token === 'string' ? token.length : 0;
            const preview = typeof token === 'string' ? token.substring(0, 10) : String(token);
            serviceLogger.log({
              name: 'AuthService.getHeaderForScope: Invalid JWT rejected',
              properties: [{ scope, tokenLength: len, segments, preview, authProvider: AuthService.authServiceInstance.constructor.name }],
            });
            throw new Error(AUTH_INVALID_TOKEN);
          }
          resolve(`Bearer ${token}`);
        })
        .catch((error: Error) => {
          serviceLogger.log({
            name: 'AuthService.getHeaderForScope: Primary auth failed',
            properties: [{ scope, authProvider: AuthService.authServiceInstance.constructor.name, haveFallbacked: this.haveFallbacked }, error],
          });
          // In MSX, MSX is the authoritative token source and MSAL cannot recover a session in
          // the embedded context (the cross-origin popup redirect-bridge response is
          // undeliverable). Never switch to MSAL here — the switch is otherwise permanent and
          // would strand the app on a provider that cannot work. Rejecting keeps MSX as the
          // primary provider so subsequent calls retry MSX and transient failures self-heal.
          if (isMsx()) {
            reject(error);
            return;
          }
          // If error occurs and we haven't fallbacked yet, try fallback to MEM auth service
          if (!this.haveFallbacked) {
            this.fallbackToMemAuthService()
              .then(() => {
                // After successful fallback, retry getting the token
                return AuthService.authServiceInstance.getTokenForScope(scope);
              })
              .then((token: string) => {
                if (!isValidJwt(token)) {
                  throw new Error(AUTH_INVALID_TOKEN);
                }
                resolve(`Bearer ${token}`);
              })
              .catch((fallbackError: Error) => {
                serviceLogger.error({
                  error: new Error('AuthService.getHeaderForScope: Both MSX and MEM auth failed'),
                  properties: [{ scope }, error, fallbackError],
                });
                reject(fallbackError);
              });
          } else {
            // If we already fallbacked or fallback not available, reject with original error
            reject(error);
          }
        });
    });
  }
}

export default AuthService.Instance;
